import React, { ReactNode, createContext, use, useEffect, useMemo, useState } from 'react'
import { mergeProps, useHover, useOption, Key } from 'react-aria'
import { ListState, SelectState } from 'react-stately'
import styled from 'styled-components'
import { IconCheckmark, IconChevronRight } from '@chilipiper/icons/figma-generated/src/icon'
import { Icon } from '../../../../new/core-components/icon/Icon'
import { Box, Flex, Text } from '../../../core-components'
import { ThemeColors, ThemeShadows } from '../../../theme'
import { EndContentProvider } from '../../data'
import { CoreCheckbox } from '../checkbox/CoreCheckbox'
import { useSelectItemData } from '../base-select/SelectItemContext'
import { DynamicItem } from '../base-select/dynamicItemUtils'

const StyledOption = styled(Box)<{ focusBg?: ThemeColors }>`
  outline: none;
  cursor: pointer;

  :hover:not([aria-disabled='true']) {
    background: ${({ theme }) => theme.colors['bg/list-item-hovered']};
  }
  background: ${({ theme, focusBg }) => focusBg && theme.colors[focusBg]};

  &[aria-disabled='true'] {
    cursor: default;
  }
`

const ActiveItemContext = createContext<{
  onSelect: (value: Key) => void
}>({
  onSelect: () => null,
})
export const ActiveItemProvider = ({
  children,
  onSelect,
}: {
  children: ReactNode
  onSelect: (value: Key) => void
}) => {
  const value = useMemo(() => ({ onSelect }), [onSelect])
  return <ActiveItemContext value={value}>{children}</ActiveItemContext>
}

/** Renders what is in the select list */
export const SelectItemInternal = ({
  item,
  state,
  variant,
}: {
  item: NonNullable<ReturnType<SelectState<DynamicItem>['collection']['getItem']>>
  state: SelectState<DynamicItem> | ListState<DynamicItem>
  variant?: 'regular' | 'multi'
}) => {
  const ref = React.useRef<HTMLLIElement>(null)
  const { optionProps, isSelected, isDisabled, isFocused } = useOption(
    { key: item.key, shouldSelectOnPressUp: true },
    state,
    ref
  )
  const [initiallySelected] = useState(isSelected)
  const { dataId, dataTestId, endContent, children, description, isHidden } = useSelectItemData(
    item.key
  )
  const { bg, boxShadow, color } = getItemStyle(isSelected, isDisabled)
  const showSelectedOnRight = isSelected && variant === 'regular'
  const activeItemContext = use(ActiveItemContext)

  const { hoverProps } = useHover({
    onHoverStart: () => {
      activeItemContext.onSelect(item.key)
    },
  })

  useEffect(() => {
    if (variant === 'regular' && ref.current && initiallySelected) {
      ref.current.scrollIntoView({ block: 'nearest' })
    }
  }, [variant, initiallySelected])

  return (
    <StyledOption
      as='li'
      display={isHidden ? 'none' : 'flex'}
      ref={ref}
      data-id={dataId}
      data-test-id={dataTestId}
      bg={bg}
      boxShadow={boxShadow}
      px={5}
      py={4}
      gap={4}
      focusBg={isFocused ? 'bg/list-item-hovered' : undefined}
      {...mergeProps(optionProps, hoverProps)}
    >
      {variant === 'multi' && (
        <CoreCheckbox isChecked={isSelected} isDisabled={isDisabled} isMixedCheck={false} />
      )}
      <Flex gap={2} flexDirection='column' flexGrow={1} maxWidth='100%'>
        <Flex h={4} alignItems='center' justifyContent='space-between'>
          {typeof children === 'string' || item.value?.itemType === 'cascade' ? (
            <Text
              color={color}
              textStyle={isSelected ? 'item-selected' : 'item-unselected'}
              minWidth={0 /* Needed for overflow ellipsis */}
            >
              {item.rendered}
            </Text>
          ) : (
            item.rendered
          )}
          {(Boolean(endContent) || showSelectedOnRight || item.value?.itemType === 'cascade') && (
            <EndContent
              endContent={endContent}
              showSelectedOnRight={showSelectedOnRight}
              isCascade={item.value?.itemType === 'cascade'}
            />
          )}
        </Flex>
        {description && (
          <Text color='fg/moderate' textStyle='body-multiline-tight'>
            {description}
          </Text>
        )}
      </Flex>
    </StyledOption>
  )
}

const EndContent = ({
  endContent,
  showSelectedOnRight,
  isCascade,
}: {
  endContent?: React.ReactNode
  isCascade: boolean
  showSelectedOnRight: boolean
}) => {
  return (
    <Flex gap={2} ml={3} alignItems='center'>
      {Boolean(endContent) && <EndContentProvider value={{}}>{endContent}</EndContentProvider>}
      {showSelectedOnRight && !isCascade && <Icon icon={IconCheckmark} color='fg/major' size={4} />}
      {isCascade && (
        <Icon
          icon={IconChevronRight}
          color={showSelectedOnRight ? 'fg/major' : 'fg/moderate'}
          size={4}
        />
      )}
    </Flex>
  )
}

const getItemStyle = (
  isSelected: boolean,
  isDisabled: boolean
): { bg: ThemeColors; boxShadow: ThemeShadows; color: ThemeColors } => {
  if (isDisabled) {
    return { bg: 'bg/disabled', boxShadow: 'border/row', color: 'fg/disabled' }
  }
  if (isSelected) {
    return {
      bg: 'bg/list-item-selected',
      boxShadow: 'border/list-item-selected',
      color: 'fg/major',
    }
  }
  return {
    bg: 'bg/list-item-default',
    boxShadow: 'border/row',
    color: 'fg/major',
  }
}

type PublicProps = {
  'data-id'?: string
  'data-test-id'?: string
  description?: string
  isDisabled?: boolean
  value: string
} & (
  | {
      children: string
      startContent?: React.ReactNode
      endContent?: React.ReactNode
      textValue?: never
    }
  | { children: React.ReactNode; startContent?: never; endContent?: never; textValue: string }
)

export const SelectItemPublic = SelectItemInternal as unknown as React.FC<PublicProps>
